import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"

export const COOKIE_CONSENT_STORAGE_KEY = "zielarz-cookie-consent"

export type CookieConsentChoice = "accepted" | "declined"

type CookieConsentBannerProps = {
  onChoiceChange: (choice: CookieConsentChoice) => void
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

export function readCookieConsent(): CookieConsentChoice | null {
  if (typeof window === "undefined") {
    return null
  }

  const storedChoice = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)

  return storedChoice === "accepted" || storedChoice === "declined"
    ? storedChoice
    : null
}

function updateGoogleConsent(choice: CookieConsentChoice) {
  const consentValue = choice === "accepted" ? "granted" : "denied"

  window.gtag?.("consent", "update", {
    ad_storage: consentValue,
    analytics_storage: consentValue,
    ad_user_data: consentValue,
    ad_personalization: consentValue,
  })
}

export function CookieConsentBanner({
  onChoiceChange,
}: CookieConsentBannerProps) {
  const [choice, setChoice] = useState<CookieConsentChoice | null>(() =>
    readCookieConsent()
  )

  useEffect(() => {
    if (choice) {
      updateGoogleConsent(choice)
      onChoiceChange(choice)
    }
  }, [choice, onChoiceChange])

  function saveChoice(nextChoice: CookieConsentChoice) {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, nextChoice)
    setChoice(nextChoice)
  }

  if (choice) {
    return null
  }

  return (
    <aside
      aria-label="Zgoda na pliki cookie"
      className="fixed right-4 bottom-4 left-4 z-[90] mx-auto max-w-3xl rounded-2xl border border-border bg-background/95 p-4 shadow-2xl shadow-black/20 backdrop-blur-xl sm:right-6 sm:bottom-6 sm:left-auto sm:w-[420px]"
    >
      <div className="space-y-3">
        <div>
          <h2 className="text-base font-bold text-foreground">
            Pliki cookie i analityka
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            Uzywamy Google Analytics i Google Tag Manager do pomiaru ruchu oraz
            poprawy strony. Mozesz zaakceptowac albo odrzucic analityczne pliki
            cookie.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="h-10 px-4"
            onClick={() => saveChoice("declined")}
          >
            Odrzucam
          </Button>
          <Button
            type="button"
            className="h-10 bg-zielarz-green px-4 text-white hover:bg-zielarz-green-light"
            onClick={() => saveChoice("accepted")}
          >
            Akceptuje
          </Button>
        </div>
      </div>
    </aside>
  )
}
